import { createTheme } from '@mui/material/styles';

/**
 * Supabase Design System Theme for VRP Application
 * Based on the migration plan specifications from vrp-mui-toolpad-migration-plan.html
 * 
 * Key Features:
 * - CSS Variables theme for dark mode support
 * - Supabase color palette
 * - Inter font family
 * - Consistent spacing and border radius
 */

// Supabase Color Palette
const supabaseColors = {
  // Brand Colors
  brandPrimary: '#3ECF8E',      // Supabase Green
  brandSecondary: '#1F2937',    // Dark Gray
  
  // Status Colors
  success: '#10B981',           // Success Green
  warning: '#F59E0B',           // Warning Amber
  error: '#EF4444',             // Error Red
  info: '#3B82F6',              // Info Blue
  
  // Background Colors
  backgroundDefault: '#F3F4F6', // Light Gray Background
  backgroundPaper: '#FFFFFF',   // White Paper
  
  // Surface Colors
  surfacePrimary: '#FFFFFF',    // White Surface
  surfaceSecondary: '#F9FAFB',  // Light Gray Surface
  surfaceHover: '#F3F4F6',      // Hover Gray
  
  // Text Colors
  textPrimary: '#111827',       // Dark Gray Text
  textSecondary: '#6B7280',     // Medium Gray Text
  textMuted: '#9CA3AF',         // Light Gray Text
  
  // Border Colors
  borderMuted: '#E5E7EB',       // Light Border
  borderDefault: '#D1D5DB',     // Medium Border
  
  // Accent Colors
  accentPrimary: '#3ECF8E',     // Primary Accent
  accentLight: '#ECFDF5',       // Light Accent Background
  accentMuted: '#D1FAE5',       // Muted Accent Background
};

// Dark Mode Color Overrides
const darkModeColors = {
  // Background Colors (Dark)
  backgroundDefault: '#0F172A',  // Dark Slate
  backgroundPaper: '#1F2937',    // Dark Gray
  
  // Surface Colors (Dark)
  surfacePrimary: '#1F2937',     // Dark Gray
  surfaceSecondary: '#374151',   // Medium Dark Gray
  surfaceHover: '#4B5563',       // Lighter Dark Gray
  
  // Text Colors (Dark)
  textPrimary: '#F9FAFB',        // Light Text
  textSecondary: '#D1D5DB',      // Medium Light Text
  textMuted: '#9CA3AF',          // Muted Light Text
  
  // Border Colors (Dark)
  borderMuted: '#374151',        // Dark Border
  borderDefault: '#4B5563',      // Medium Dark Border
};

// Typography Scale
const typography = {
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  h1: {
    fontSize: '36px',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  button: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.4,
    textTransform: 'none' as const,
  },
};

// Spacing and Layout
const spacing = 4; // 4px base unit

const shape = {
  borderRadius: 6, // 6px border radius
};

// Shadow System
const shadows = [
  'none',
  '0 1px 3px rgba(0, 0, 0, 0.1)',
  '0 2px 8px rgba(0, 0, 0, 0.1)',
  '0 4px 12px rgba(0, 0, 0, 0.1)',
  '0 8px 24px rgba(0, 0, 0, 0.1)',
  // ... additional shadow levels
] as const;

// Create the main Supabase theme
export const supabaseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: supabaseColors.brandPrimary,
      light: supabaseColors.accentLight,
      dark: supabaseColors.success,
    },
    secondary: {
      main: supabaseColors.brandSecondary,
    },
    success: {
      main: supabaseColors.success,
    },
    warning: {
      main: supabaseColors.warning,
    },
    error: {
      main: supabaseColors.error,
    },
    info: {
      main: supabaseColors.info,
    },
    background: {
      default: supabaseColors.backgroundDefault,
      paper: supabaseColors.backgroundPaper,
    },
    text: {
      primary: supabaseColors.textPrimary,
      secondary: supabaseColors.textSecondary,
    },
  },
  typography,
  spacing,
  shape,
  shadows: shadows as any,
  components: {
    // MUI Toolpad AppProvider / DashboardLayout Navigation Styling
    MuiListItemButton: {
      styleOverrides: {
        root: {
          // Supabase navigation item specifications
          height: '40px',
          padding: '8px 16px',
          borderRadius: '6px',
          margin: '2px 8px',
          fontSize: '14px',
          fontWeight: 500,
          color: supabaseColors.textSecondary,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: supabaseColors.surfaceHover,
            color: supabaseColors.textPrimary,
          },
          '&.Mui-selected': {
            backgroundColor: supabaseColors.accentMuted,
            color: supabaseColors.accentPrimary,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '24px',
              backgroundColor: supabaseColors.accentPrimary,
              borderRadius: '0 2px 2px 0',
            },
            '&:hover': {
              backgroundColor: supabaseColors.accentMuted,
              color: supabaseColors.accentPrimary,
            },
          },
          // Icon styling within navigation items
          '& .MuiListItemIcon-root': {
            minWidth: '32px',
            '& .MuiSvgIcon-root': {
              fontSize: '16px',
              opacity: 0.8,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '32px',
          color: 'inherit',
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
            opacity: 0.8,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
          '& .MuiTypography-root': {
            fontSize: '14px',
            fontWeight: 500,
          },
        },
      },
    },
    // Navigation container (sidebar) styling
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: supabaseColors.surfaceSecondary,
          borderRight: `1px solid ${supabaseColors.borderMuted}`,
          width: '240px',
        },
      },
    },
    // Button Styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    // Card Styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${supabaseColors.borderMuted}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Paper Styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // Input Styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            height: '40px',
            '& fieldset': {
              borderColor: supabaseColors.borderMuted,
            },
            '&:hover fieldset': {
              borderColor: supabaseColors.borderDefault,
            },
            '&.Mui-focused fieldset': {
              borderColor: supabaseColors.brandPrimary,
            },
          },
        },
      },
    },
    // Table Styling
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: supabaseColors.surfaceSecondary,
          '& .MuiTableCell-head': {
            fontWeight: 500,
            color: supabaseColors.textPrimary,
            borderBottom: `1px solid ${supabaseColors.borderMuted}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: supabaseColors.surfaceHover,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${supabaseColors.borderMuted}`,
          padding: '12px 16px',
        },
      },
    },
    // Chip Styling
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '12px',
          fontWeight: 500,
        },
      },
    },
  },
});

// Dark theme variant
export const supabaseDarkTheme = createTheme({
  ...supabaseTheme,
  palette: {
    ...supabaseTheme.palette,
    mode: 'dark',
    background: {
      default: darkModeColors.backgroundDefault,
      paper: darkModeColors.backgroundPaper,
    },
    text: {
      primary: darkModeColors.textPrimary,
      secondary: darkModeColors.textSecondary,
    },
  },
  components: {
    ...supabaseTheme.components,
    // Dark mode specific overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${darkModeColors.borderMuted}`,
          backgroundColor: darkModeColors.surfacePrimary,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: darkModeColors.surfaceSecondary,
          '& .MuiTableCell-head': {
            fontWeight: 500,
            color: darkModeColors.textPrimary,
            borderBottom: `1px solid ${darkModeColors.borderMuted}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: darkModeColors.surfaceHover,
          },
        },
      },
    },
  },
});

// CSS Variables theme (recommended for Toolpad)
export const supabaseCSSVarsTheme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      main: supabaseColors.brandPrimary,
    },
    secondary: {
      main: supabaseColors.brandSecondary,
    },
    background: {
      default: supabaseColors.backgroundDefault,
      paper: supabaseColors.backgroundPaper,
    },
    text: {
      primary: supabaseColors.textPrimary,
      secondary: supabaseColors.textSecondary,
    },
  },
  typography,
  spacing,
  shape,
  components: supabaseTheme.components,
});

// Export theme variants for different use cases
export const themes = {
  light: supabaseTheme,
  dark: supabaseDarkTheme,
  cssVars: supabaseCSSVarsTheme,
};

// Export color tokens for direct use
export { supabaseColors, darkModeColors };

// Default export - CSS Variables theme (recommended for Toolpad)
export default supabaseCSSVarsTheme;